import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { v4 as uuidv4 } from "uuid";
import { AutoComplete, AutoCompleteUpdate } from "@/model";
import { useUpdateAutocomplete } from "@/query/autocomplete";
import { useAgentId, useModal } from "@/hook";
import Button from "@/component/Button";
import { Modal } from "@/component/modal";
import { TextInputGroup } from "@/component/input";
import { errorToast, successToast } from "@/component/Toast";
import RefreshIcon from "@/assets/svgs/refresh_autocomplete.svg";


export const AUTOCOMPLETE_MODIFY_MODAL_ID = "autocomplete-modify-modal-id";

const AutocompleteModifyModal: React.FC = () => {
    const intl = useIntl();
    const agentId = useAgentId();
    const modal = useModal();
    const autocomplete = modal.getData(AUTOCOMPLETE_MODIFY_MODAL_ID) as AutoComplete;

    const [validation, setValidation] = useState<{ name: boolean, token: boolean }>({ name: true, token: true });
    const [name, setName] = useState<string>();
    const [token, setToken] = useState<string>();

    const { mutateAsync: updateAutocomplete, isPending: isUpdating } = useUpdateAutocomplete(agentId);

    const onClickCancel = () => {
        modal.close(AUTOCOMPLETE_MODIFY_MODAL_ID);
    };

    const onClickConfirm = async () => {
        const currentValidation = {
            name: !!name && name.trim().length > 0,
            token: !!token && token.trim().length > 0
        };
        setValidation(currentValidation);
        if (Object.values(currentValidation).some(value => !value)) {
            return;
        }

        const form: AutoCompleteUpdate = {
            agent_id: agentId,
            autocomplete_id: autocomplete.id,
            title: name!,
            token: token!,
        };

        await updateAutocomplete(form)
            .then(() => {
                successToast(intl.formatMessage({ id: "t003" }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t004" }));
            })
            .finally(() => {
                modal.close(AUTOCOMPLETE_MODIFY_MODAL_ID);
            });
    };

    useEffect(() => {
        if (!autocomplete) {
            return;
        }
        setName(autocomplete.name);
        setToken(autocomplete.token);
    }, [autocomplete]);

    return (
        <Modal id={AUTOCOMPLETE_MODIFY_MODAL_ID}
               size="sm"
               title={intl.formatMessage({ id: "i08004" })}
        >
            <div className="modal-body">
                <TextInputGroup value={name ?? ""}
                                label={intl.formatMessage({ id: "i08011" })}
                                placeholder={intl.formatMessage({ id: "i08015" })}
                                invalid={!validation.name}
                                onChange={setName}
                />
                <TokenInput value={token ?? ""}
                            invalid={!validation.token}
                            label={intl.formatMessage({ id: "i08012" })}
                            description={intl.formatMessage({ id: "i08013" })}
                            onChange={setToken}
                />
                <div className="modal-btn-group">
                    <Button variant="secondary"
                            size="sm"
                            disabled={isUpdating}
                            onClick={onClickCancel}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button variant="primary"
                            size="sm"
                            disabled={isUpdating}
                            onClick={onClickConfirm}
                    >
                        {intl.formatMessage({ id: "b0001" })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

interface TokenInputProps {
    value?: string;
    label: string;
    description: string;
    invalid: boolean;
    onChange: (value: string) => void;
}

const TokenInput: React.FC<TokenInputProps> = (props) => {
    const { value, label, description, onChange, invalid } = props;

    const intl = useIntl();

    const inputRef = useRef<HTMLInputElement>(null);

    const refreshToken = () => {
        const uuid = uuidv4();
        onChange(uuid);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        onChange(value);
    };

    return (
        <div className="flex flex-col gap-1.5">
            <div className="input-group-heading">
                {label}
            </div>
            <div className="flex items-center gap-2">
                <input type="text"
                       ref={inputRef}
                       className={classNames(
                           "input-default",
                           invalid && "input-invalid"
                       )}
                       value={value ?? ""}
                       placeholder={intl.formatMessage({ id: "i08016" })}
                       onChange={handleChange}
                />
                <img src={RefreshIcon}
                     alt="refresh icon"
                     className="size-4.5 cursor-pointer"
                     onClick={refreshToken}
                />
            </div>
            <div className="font-[400] text-[12px] leading-[1.5] text-black-400">
                {description}
            </div>
        </div>
    );
};

export default AutocompleteModifyModal;
